import { Fragment, useMemo, useState } from "react";
import styles from "./ForgotPassword.module.scss";
import CommonFormFieldWrapper from "../../common/components/form-field-wrapper/CommonFormFieldWrapper";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import AuthentificationService from "../../httpService/AuthentificationService";
import { BounceLoader } from "react-spinners";

type ForgotPasswordFormProps = {
	password: string;
	confirmPassword: string;
};

const ForgotPassword = () => {
	const { token } = useParams();
	const [isPasswordVisible, setIsPasswordVisible] = useState(false);
	const [isConfirmPasswordVisible, setIsConfirmPasswordVisible] = useState(false);
	const [loading, setLoading] = useState(false);
	const authService = useMemo(() => new AuthentificationService(), []);
	const navigate = useNavigate();

	const {
		register,
		handleSubmit,
		formState: { isValid },
	} = useForm<ForgotPasswordFormProps>({
		defaultValues: {
			password: "",
			confirmPassword: "",
		},
		reValidateMode: "onSubmit",
		mode: "onTouched",
	});

	const onSubmit = async (data: ForgotPasswordFormProps) => {
		if (!isValid && !token) return;

		const { password, confirmPassword } = data;
		if (password !== confirmPassword) {
			toast.error("Passwords do not match");
			return;
		}

		const passwordValidation = validatePassword(password);
		if (passwordValidation !== true) {
			toast.error(passwordValidation);
			return;
		}

		setLoading(true);

		try {
			await authService.post(`forgot-password/${token}`, { password });
			navigate("/login");
			toast.success("Password reset successfully");
			setLoading(false);
		} catch (error: any) {
			const errorText: any = error.response?.data?.title;
			toast.error(errorText ?? "An unexpected error occurred");
			setLoading(false);
		}
	};

	const validatePassword = (value: string) => {
		if (value.length === 0) {
			return "Password is required";
		}

		if (value.length < 10) {
			return "Password must be at least 10 characters long";
		}

		if (!/[A-Z]/.test(value)) {
			return "Password must contain at least one uppercase letter";
		}

		if (!/[a-z]/.test(value)) {
			return "Password must contain at least one lowercase letter";
		}

		if (!/\d/.test(value)) {
			return "Password must contain at least one number";
		}

		if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]+/.test(value)) {
			return "Password must contain at least one special character";
		}

		return true;
	};

	return (
		<Fragment>
			<div className={styles.aboveContainer}>
				<div className={`link `}>Need Help?</div>
			</div>
			<div className={styles.container}>
				<div className={styles.header}>Set New Password</div>

				<form className={styles.contentContainer} onSubmit={handleSubmit(onSubmit)}>
					<CommonFormFieldWrapper title="New password">
						<input
							type={isPasswordVisible ? "text" : "password"}
							{...register("password", { required: "Password is required" })}
							placeholder="Enter New Password"
						/>
						<div className={styles.eye} onClick={() => setIsPasswordVisible(prev => !prev)}>
							{!isPasswordVisible ? (
								<svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M4 4L20 20" stroke="#000000" strokeWidth="2" strokeLinecap="round" />
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M6.22308 5.63732C4.19212 6.89322 2.60069 8.79137 1.73175 11.0474C1.49567 11.6604 1.49567 12.3396 1.73175 12.9526C3.31889 17.0733 7.31641 20 12 20C14.422 20 16.6606 19.2173 18.4773 17.8915L17.042 16.4562C15.6033 17.4309 13.8678 18 12 18C8.17084 18 4.89784 15.6083 3.5981 12.2337C3.54022 12.0835 3.54022 11.9165 3.5981 11.7663C4.36731 9.76914 5.82766 8.11625 7.6854 7.09964L6.22308 5.63732ZM9.47955 8.89379C8.5768 9.6272 7.99997 10.7462 7.99997 12C7.99997 14.2091 9.79083 16 12 16C13.2537 16 14.3728 15.4232 15.1062 14.5204L13.6766 13.0908C13.3197 13.6382 12.7021 14 12 14C10.8954 14 9.99997 13.1046 9.99997 12C9.99997 11.2979 10.3618 10.6802 10.9091 10.3234L9.47955 8.89379ZM15.9627 12.5485L11.4515 8.03729C11.6308 8.0127 11.8139 8 12 8C14.2091 8 16 9.79086 16 12C16 12.1861 15.9873 12.3692 15.9627 12.5485ZM18.5678 15.1536C19.3538 14.3151 19.9812 13.3259 20.4018 12.2337C20.4597 12.0835 20.4597 11.9165 20.4018 11.7663C19.1021 8.39172 15.8291 6 12 6C11.2082 6 10.4402 6.10226 9.70851 6.29433L8.11855 4.70437C9.32541 4.24913 10.6335 4 12 4C16.6835 4 20.681 6.92668 22.2682 11.0474C22.5043 11.6604 22.5043 12.3396 22.2682 12.9526C21.7464 14.3074 20.964 15.5331 19.9824 16.5682L18.5678 15.1536Z"
										fill="#000000"
									/>
								</svg>
							) : (
								<svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M6.30147 15.5771C4.77832 14.2684 3.6904 12.7726 3.18002 12C3.6904 11.2274 4.77832 9.73158 6.30147 8.42294C7.87402 7.07185 9.81574 6 12 6C14.1843 6 16.1261 7.07185 17.6986 8.42294C19.2218 9.73158 20.3097 11.2274 20.8201 12C20.3097 12.7726 19.2218 14.2684 17.6986 15.5771C16.1261 16.9282 14.1843 18 12 18C9.81574 18 7.87402 16.9282 6.30147 15.5771ZM12 4C9.14754 4 6.75717 5.39462 4.99812 6.90595C3.23268 8.42276 2.00757 10.1376 1.46387 10.9698C1.05306 11.5985 1.05306 12.4015 1.46387 13.0302C2.00757 13.8624 3.23268 15.5772 4.99812 17.0941C6.75717 18.6054 9.14754 20 12 20C14.8525 20 17.2429 18.6054 19.002 17.0941C20.7674 15.5772 21.9925 13.8624 22.5362 13.0302C22.947 12.4015 22.947 11.5985 22.5362 10.9698C21.9925 10.1376 20.7674 8.42276 19.002 6.90595C17.2429 5.39462 14.8525 4 12 4ZM10 12C10 10.8954 10.8955 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8955 14 10 13.1046 10 12ZM12 8C9.7909 8 8.00004 9.79086 8.00004 12C8.00004 14.2091 9.7909 16 12 16C14.2092 16 16 14.2091 16 12C16 9.79086 14.2092 8 12 8Z"
										fill="#000000"
									/>
								</svg>
							)}
						</div>
					</CommonFormFieldWrapper>
					<CommonFormFieldWrapper title="Confirm password">
						<input
							type={isConfirmPasswordVisible ? "text" : "password"}
							{...register("confirmPassword", { required: "Password must be confirmed" })}
							placeholder="Confirm password"
						/>
						<div className={styles.eye} onClick={() => setIsConfirmPasswordVisible(prev => !prev)}>
							{!isConfirmPasswordVisible ? (
								<svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path d="M4 4L20 20" stroke="#000000" strokeWidth="2" strokeLinecap="round" />
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M6.22308 5.63732C4.19212 6.89322 2.60069 8.79137 1.73175 11.0474C1.49567 11.6604 1.49567 12.3396 1.73175 12.9526C3.31889 17.0733 7.31641 20 12 20C14.422 20 16.6606 19.2173 18.4773 17.8915L17.042 16.4562C15.6033 17.4309 13.8678 18 12 18C8.17084 18 4.89784 15.6083 3.5981 12.2337C3.54022 12.0835 3.54022 11.9165 3.5981 11.7663C4.36731 9.76914 5.82766 8.11625 7.6854 7.09964L6.22308 5.63732ZM9.47955 8.89379C8.5768 9.6272 7.99997 10.7462 7.99997 12C7.99997 14.2091 9.79083 16 12 16C13.2537 16 14.3728 15.4232 15.1062 14.5204L13.6766 13.0908C13.3197 13.6382 12.7021 14 12 14C10.8954 14 9.99997 13.1046 9.99997 12C9.99997 11.2979 10.3618 10.6802 10.9091 10.3234L9.47955 8.89379ZM15.9627 12.5485L11.4515 8.03729C11.6308 8.0127 11.8139 8 12 8C14.2091 8 16 9.79086 16 12C16 12.1861 15.9873 12.3692 15.9627 12.5485ZM18.5678 15.1536C19.3538 14.3151 19.9812 13.3259 20.4018 12.2337C20.4597 12.0835 20.4597 11.9165 20.4018 11.7663C19.1021 8.39172 15.8291 6 12 6C11.2082 6 10.4402 6.10226 9.70851 6.29433L8.11855 4.70437C9.32541 4.24913 10.6335 4 12 4C16.6835 4 20.681 6.92668 22.2682 11.0474C22.5043 11.6604 22.5043 12.3396 22.2682 12.9526C21.7464 14.3074 20.964 15.5331 19.9824 16.5682L18.5678 15.1536Z"
										fill="#000000"
									/>
								</svg>
							) : (
								<svg width="30px" height="30px" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										fillRule="evenodd"
										clipRule="evenodd"
										d="M6.30147 15.5771C4.77832 14.2684 3.6904 12.7726 3.18002 12C3.6904 11.2274 4.77832 9.73158 6.30147 8.42294C7.87402 7.07185 9.81574 6 12 6C14.1843 6 16.1261 7.07185 17.6986 8.42294C19.2218 9.73158 20.3097 11.2274 20.8201 12C20.3097 12.7726 19.2218 14.2684 17.6986 15.5771C16.1261 16.9282 14.1843 18 12 18C9.81574 18 7.87402 16.9282 6.30147 15.5771ZM12 4C9.14754 4 6.75717 5.39462 4.99812 6.90595C3.23268 8.42276 2.00757 10.1376 1.46387 10.9698C1.05306 11.5985 1.05306 12.4015 1.46387 13.0302C2.00757 13.8624 3.23268 15.5772 4.99812 17.0941C6.75717 18.6054 9.14754 20 12 20C14.8525 20 17.2429 18.6054 19.002 17.0941C20.7674 15.5772 21.9925 13.8624 22.5362 13.0302C22.947 12.4015 22.947 11.5985 22.5362 10.9698C21.9925 10.1376 20.7674 8.42276 19.002 6.90595C17.2429 5.39462 14.8525 4 12 4ZM10 12C10 10.8954 10.8955 10 12 10C13.1046 10 14 10.8954 14 12C14 13.1046 13.1046 14 12 14C10.8955 14 10 13.1046 10 12ZM12 8C9.7909 8 8.00004 9.79086 8.00004 12C8.00004 14.2091 9.7909 16 12 16C14.2092 16 16 14.2091 16 12C16 9.79086 14.2092 8 12 8Z"
										fill="#000000"
									/>
								</svg>
							)}
						</div>
					</CommonFormFieldWrapper>

					<button className={`standardButton ${styles.button} ${isValid ? "" : styles.disabledButton}`} type="submit">
						{loading ? (
							<BounceLoader color="#DE841B" size={25} />
						) : (
							<>
								Submit
								<svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
									<path
										d="M4.94665 6.00047L0.873576 1.92737C0.735109 1.78892 0.664276 1.61489 0.661076 1.40527C0.657859 1.19568 0.728692 1.01844 0.873576 0.873575C1.01844 0.728692 1.19408 0.65625 1.40048 0.65625C1.60688 0.65625 1.78251 0.728692 1.92738 0.873575L6.4216 5.3678C6.5152 5.46138 6.58123 5.5601 6.61968 5.66395C6.65814 5.76778 6.67738 5.87996 6.67738 6.00047C6.67738 6.12099 6.65814 6.23317 6.61968 6.337C6.58123 6.44085 6.5152 6.53957 6.4216 6.63315L1.92738 11.1274C1.78893 11.2658 1.61489 11.3367 1.40527 11.3399C1.19568 11.3431 1.01844 11.2723 0.873576 11.1274C0.728692 10.9825 0.65625 10.8069 0.65625 10.6005C0.65625 10.3941 0.728692 10.2184 0.873576 10.0736L4.94665 6.00047Z"
										fill="#F68B1E"
									/>
								</svg>
							</>
						)}
					</button>
				</form>
			</div>
		</Fragment>
	);
};

export default ForgotPassword;
